import { useEffect, useState, type FormEvent } from 'react';
import styled, { css } from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import {
  useAdminCreateUserMutation,
  useAdminDeleteUserMutation,
  useAdminDisable2faMutation,
  useAdminListUsersQuery,
  useAdminUpdateUserMutation,
  useMeQuery,
  type AdminUser,
} from '@shared/api';
import { realtime, type AgentEvent } from '@shared/realtime/ws';
import { pulse } from '@shared/theme/animations';

const Toolbar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const SearchInput = styled(Input)`
  max-width: 320px;
`;

const FilterSelect = styled(Select)`
  width: 160px;
`;

const TableCard = styled(Card)`
  padding: 0;
  /* Горизонтальный скролл если строка не помещается. Без этого с колонкой
     «2FA» + действиями (3-4 кнопки) последняя кнопка обрезается. */
  overflow-x: auto;
  overflow-y: hidden;

  table {
    min-width: 1100px;
  }
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmailCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

/** Зелёный пульсирующий индикатор онлайн-статуса рядом с email. Онлайн —
 *  status.success + готовая pulse-анимация (animations.ts); офлайн —
 *  приглушённая серая точка без анимации. */
const PresenceDot = styled.span<{ $online: boolean }>`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $online, theme }) =>
    $online ? theme.colors.status.success : theme.colors.border};
  ${({ $online }) =>
    $online
      ? css`
          animation: ${pulse} 2s ease-in-out infinite;
        `
      : ''}
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing(8)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  justify-content: flex-end;
  flex-wrap: nowrap;

  & > button {
    white-space: nowrap;
  }
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  justify-content: flex-end;
`;

const ROLE_HINTS: Record<string, string> = {
  admin: 'Полный технический и управленческий доступ',
  director: 'Полный доступ к ОСНОВА.Гендиректор, ОСНОВА.Руководитель и всем модулям платформы',
  manager: 'Руководитель компании: полный R+W доступ ко всем модулям, включая ОСНОВА.Гендиректор',
  pm_specialist: 'ОСНОВА.Руководитель (R+W) и чтение ОСНОВА.Гендиректор и смежных модулей объекта',
  pm: 'Менеджер проекта (ПТО, Снабжение, Кадры)',
  lawyer: 'ОСНОВА.Юрист и ОСНОВА.Диспетчер',
  dispatcher_operator: 'ОСНОВА.Диспетчер',
  estimator: 'ОСНОВА.Сметчик',
  estimator_plus: 'ОСНОВА.Сметчик (расширенный доступ)',
  chief_estimator:
    'ОСНОВА.Сметчик (начальник СЦО и заместитель: расширенный доступ и право возвращать архивные периоды индексов)',
  economist: 'ОСНОВА.Экономист (R+W) и просмотр ОСНОВА.Финансист / ОСНОВА.Гендиректор',
  economist_plus:
    'ОСНОВА.Экономист (R+W), просмотр ОСНОВА.Финансист и ведение БДДС/счетов в ОСНОВА.Гендиректор',
  chief_economist:
    'ОСНОВА.Экономист (R+W) и право утверждать: решения по обработкам БДР, версии плана, шаблоны отчётов, ПРЦ',
  financier: 'Управление казначейством, фабрикой платежей, налогами ЕНС и зарплатными реестрами',
  pto_engineer: 'ОСНОВА.Инженер (ПТО)',
  engineer: 'ОСНОВА.Инженер',
  engineer_plus: 'ОСНОВА.Инженер (с правом удаления)',
  foreman: 'ОСНОВА.Прораб',
  site_chief: 'ОСНОВА.Прораб (начальник участка)',
  field_specialist_plus: 'ОСНОВА.Прораб (расширенный доступ)',
  ksp_engineer: 'ОСНОВА.КСП (графики ГПР и КСГ)',
  mto_specialist: 'ОСНОВА.МТО',
  mto_specialist_plus: 'ОСНОВА.МТО+',
  supplier: 'Служба снабжения',
  logist: 'ОСНОВА.Логист (ЭТрН и рейсы)',
  surveyor: 'ОСНОВА.Геодезист',
  meeting_organizer: 'ОСНОВА.Совещания (R+W)',
  hr: 'ОСНОВА.Кадры (рекрутер)',
  hr_admin: 'ОСНОВА.Кадры (администратор подбора)',
  hr_auditor: 'ОСНОВА.Кадры (аудитор без ПДн)',
  employee: 'ОСНОВА.Диск, Помощник и «Что нового» — без доступа к агентам',
  disk_name_alias: 'В ОСНОВА.Диске вместо имени — «Администратор»; администраторы видят имя при наведении',
};

// Каждая роль списка должна быть заведена миграцией (таблица roles) — проверяет
// roles.test.mjs: роль без миграции назначить нельзя (сервер ответит NotFound).
const ROLES: Array<{ value: string; label: string; hint?: string }> = [
  { value: 'employee', label: 'Сотрудник', hint: ROLE_HINTS.employee },
  { value: 'admin', label: 'Администратор', hint: ROLE_HINTS.admin },
  { value: 'director', label: 'Генеральный директор', hint: ROLE_HINTS.director },
  { value: 'manager', label: 'Руководитель', hint: ROLE_HINTS.manager },
  { value: 'pm_specialist', label: 'Руководитель проекта', hint: ROLE_HINTS.pm_specialist },
  { value: 'pm', label: 'Project Manager', hint: ROLE_HINTS.pm },
  { value: 'lawyer', label: 'Юрист', hint: ROLE_HINTS.lawyer },
  { value: 'dispatcher_operator', label: 'Оператор-диспетчер', hint: ROLE_HINTS.dispatcher_operator },
  { value: 'estimator', label: 'Сметчик', hint: ROLE_HINTS.estimator },
  { value: 'estimator_plus', label: 'Сметчик+', hint: ROLE_HINTS.estimator_plus },
  { value: 'chief_estimator', label: 'Начальник СЦО', hint: ROLE_HINTS.chief_estimator },
  { value: 'economist', label: 'Экономист', hint: ROLE_HINTS.economist },
  { value: 'economist_plus', label: 'Экономист+', hint: ROLE_HINTS.economist_plus },
  { value: 'chief_economist', label: 'Главный экономист', hint: ROLE_HINTS.chief_economist },
  { value: 'financier', label: 'Финансист / Казначей', hint: ROLE_HINTS.financier },
  { value: 'pto_engineer', label: 'Инженер ПТО', hint: ROLE_HINTS.pto_engineer },
  { value: 'engineer', label: 'Инженер', hint: ROLE_HINTS.engineer },
  { value: 'engineer_plus', label: 'Инженер+', hint: ROLE_HINTS.engineer_plus },
  { value: 'foreman', label: 'Прораб', hint: ROLE_HINTS.foreman },
  { value: 'site_chief', label: 'Начальник участка', hint: ROLE_HINTS.site_chief },
  { value: 'field_specialist_plus', label: 'Прораб+', hint: ROLE_HINTS.field_specialist_plus },
  { value: 'ksp_engineer', label: 'Инженер КСП', hint: ROLE_HINTS.ksp_engineer },
  { value: 'mto_specialist', label: 'МТО', hint: ROLE_HINTS.mto_specialist },
  { value: 'mto_specialist_plus', label: 'МТО+', hint: ROLE_HINTS.mto_specialist_plus },
  { value: 'supplier', label: 'Снабжение', hint: ROLE_HINTS.supplier },
  { value: 'logist', label: 'Логист', hint: ROLE_HINTS.logist },
  { value: 'surveyor', label: 'Геодезист', hint: ROLE_HINTS.surveyor },
  { value: 'meeting_organizer', label: 'Организатор совещаний', hint: ROLE_HINTS.meeting_organizer },
  { value: 'hr', label: 'Отдел кадров', hint: ROLE_HINTS.hr },
  { value: 'hr_admin', label: 'Кадры: админ', hint: ROLE_HINTS.hr_admin },
  { value: 'hr_auditor', label: 'Кадры: аудитор', hint: ROLE_HINTS.hr_auditor },
  { value: 'disk_name_alias', label: 'Диск: скрыть имя', hint: ROLE_HINTS.disk_name_alias },
];

const RoleChip = styled.button<{ $selected: boolean }>`
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.border)};
  background: ${({ $selected, theme }) =>
    $selected ? `${theme.colors.primary}22` : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.primary : theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RoleChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RoleBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

function generatePassword(): string {
  // 16 символов: безопасный alphabet без визуально похожих (l/I/0/O).
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => alphabet[b % alphabet.length]).join('');
}

function formatDate(iso: string): string {
  // Короткий формат «17.05.26» — экономит ширину колонки в таблице юзеров,
  // где иначе последняя кнопка действий обрезается.
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/**
 * Онлайн-статусы пользователей для индикатора в таблице. Seed — из REST-ответа
 * /admin/users (поле is_online, синхронизируется при refetch/смене фильтров),
 * live-обновления — из WS presence_update. Не трогает RTK Query-кэш: индикатор
 * чисто UI, обновляется точечно без рефетча всей таблицы.
 */
function useOnlineUsers(seed: AdminUser[] | undefined) {
  const [online, setOnline] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!seed) return;
    setOnline((prev) => {
      const next = new Set(prev);
      for (const u of seed) {
        if (u.is_online) next.add(u.id);
        else next.delete(u.id);
      }
      return next;
    });
  }, [seed]);
  useEffect(() => {
    return realtime.subscribe((e: AgentEvent) => {
      if (e.event !== 'presence_update') return;
      setOnline((prev) => {
        const next = new Set(prev);
        if (e.online) next.add(e.user_id);
        else next.delete(e.user_id);
        return next;
      });
    });
  }, []);
  return online;
}

export function AdminUsersTab() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);

  const { data: me } = useMeQuery();
  const { data, isFetching } = useAdminListUsersQuery({
    role: roleFilter || undefined,
    search: search || undefined,
    active: activeFilter === 'all' ? undefined : activeFilter === 'active',
  });

  const items = data?.items ?? [];
  const online = useOnlineUsers(data?.items);
  const [updateUser] = useAdminUpdateUserMutation();
  const [deleteUserMut, { isLoading: isDeleting }] = useAdminDeleteUserMutation();
  const [adminDisable2fa] = useAdminDisable2faMutation();
  const [reset2faUser, setReset2faUser] = useState<AdminUser | null>(null);

  return (
    <>
      <Toolbar>
        <SearchInput
          placeholder="Поиск по email или имени"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterSelect
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as typeof activeFilter)}
        >
          <option value="all">Все</option>
          <option value="active">Активные</option>
          <option value="inactive">Отключённые</option>
        </FilterSelect>
        <FilterSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">Все роли</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </FilterSelect>
        <div style={{ flex: 1 }} />
        <Button $variant="primary" onClick={() => setCreateOpen(true)}>
          + Создать пользователя
        </Button>
      </Toolbar>

      <TableCard>
        {isFetching && items.length === 0 ? (
          <EmptyState>Загрузка…</EmptyState>
        ) : items.length === 0 ? (
          <EmptyState>Нет пользователей по заданным фильтрам</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Email</th>
                <th>ФИО</th>
                <th>Департамент</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>2FA</th>
                <th>Создан</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isSelf = u.id === me?.user.id;
                return (
                  <tr key={u.id}>
                    <td>
                      <EmailCell>
                        <PresenceDot $online={online.has(u.id)} />
                        <Mono>{u.email}</Mono>
                      </EmailCell>
                    </td>
                    <td>{u.full_name}</td>
                    <td>{u.department ?? '—'}</td>
                    <td>
                      {u.roles.length === 0 ? (
                        '—'
                      ) : (
                        <RoleBadgesRow>
                          {u.roles.map((r) => (
                            <Badge key={r} $tone="neutral">
                              {ROLES.find((x) => x.value === r)?.label ?? r}
                            </Badge>
                          ))}
                        </RoleBadgesRow>
                      )}
                    </td>
                    <td>
                      {u.is_active ? (
                        <Badge $tone="success">Активен</Badge>
                      ) : (
                        <Badge $tone="danger">Отключён</Badge>
                      )}
                    </td>
                    <td>
                      {u.totp_enabled ? (
                        <Badge $tone="success">on</Badge>
                      ) : (
                        <Badge $tone="warning">off</Badge>
                      )}
                    </td>
                    <td>
                      <Mono>{formatDate(u.created_at)}</Mono>
                    </td>
                    <td>
                      <ActionsCell>
                        <Button $variant="secondary" $size="sm" onClick={() => setEditUser(u)}>
                          Редактировать
                        </Button>
                        {u.is_active ? (
                          <Button
                            $variant="ghost"
                            $size="sm"
                            disabled={isSelf}
                            onClick={() =>
                              updateUser({ id: u.id, is_active: false })
                                .unwrap()
                                .catch(() => {})
                            }
                            title={isSelf ? 'Нельзя отключить себя' : 'Отключить пользователя'}
                          >
                            Отключить
                          </Button>
                        ) : (
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() =>
                              updateUser({ id: u.id, is_active: true })
                                .unwrap()
                                .catch(() => {})
                            }
                          >
                            Включить
                          </Button>
                        )}
                        {u.totp_enabled && (
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => setReset2faUser(u)}
                            title="Сбросить 2FA (если пользователь потерял телефон)"
                          >
                            Сброс 2FA
                          </Button>
                        )}
                        <Button
                          $variant="danger"
                          $size="sm"
                          disabled={isSelf}
                          onClick={() => setDeleteUser(u)}
                          title={isSelf ? 'Нельзя удалить себя' : 'Удалить пользователя'}
                        >
                          Удалить
                        </Button>
                      </ActionsCell>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </TableCard>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />

      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} />}

      <ConfirmDialog
        open={!!reset2faUser}
        title="Сбросить 2FA"
        warning={
          reset2faUser
            ? `Полностью отключит 2FA для ${reset2faUser.email}. Используйте только если пользователь потерял доступ к authenticator-приложению. После сброса пользователь сможет войти только по паролю и снова настроить 2FA.`
            : ''
        }
        confirmText={reset2faUser?.email ?? ''}
        confirmLabel="Введите email пользователя для подтверждения"
        confirmButtonLabel="Сбросить 2FA"
        busyLabel="Сброс…"
        onConfirm={async () => {
          if (!reset2faUser) return;
          await adminDisable2fa(reset2faUser.id)
            .unwrap()
            .catch(() => {});
          setReset2faUser(null);
        }}
        onCancel={() => setReset2faUser(null)}
      />

      <ConfirmDialog
        open={!!deleteUser}
        title="Удалить пользователя"
        warning={
          deleteUser
            ? `Полное удаление пользователя ${deleteUser.email}. Восстановить невозможно. Его договоры/документы останутся в системе, но без ссылки на автора.`
            : ''
        }
        confirmText={deleteUser?.email ?? ''}
        confirmLabel="Введите email пользователя для подтверждения"
        confirmButtonLabel="Удалить навсегда"
        busyLabel="Удаление…"
        isBusy={isDeleting}
        onConfirm={async () => {
          if (!deleteUser) return;
          await deleteUserMut(deleteUser.id)
            .unwrap()
            .catch(() => {});
          setDeleteUser(null);
        }}
        onCancel={() => setDeleteUser(null)}
      />
    </>
  );
}

// ─────────── Create modal ───────────

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
}

function CreateUserModal({ open, onClose }: CreateModalProps) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['lawyer']);
  function toggleRole(value: string) {
    setSelectedRoles((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  }
  const [password, setPassword] = useState(() => generatePassword());
  const [error, setError] = useState<string | null>(null);
  const [createdInfo, setCreatedInfo] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [createUser, { isLoading }] = useAdminCreateUserMutation();

  function reset() {
    setEmail('');
    setFullName('');
    setDepartment('');
    setSelectedRoles(['lawyer']);
    setPassword(generatePassword());
    setError(null);
    setCreatedInfo(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (selectedRoles.length === 0) {
      setError('Выберите хотя бы одну роль пользователя');
      return;
    }
    try {
      const res = await createUser({
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        department: department.trim() || undefined,
        roles: selectedRoles,
      }).unwrap();
      setCreatedInfo({ email: res.email, password });
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось создать пользователя';
      setError(msg);
    }
  }

  return (
    <Modal open={open} title="Новый пользователь" onClose={close}>
      {createdInfo ? (
        <CreatedView info={createdInfo} onDone={close} />
      ) : (
        <form onSubmit={onSubmit}>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Field>
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@satcom.local"
              autoComplete="off"
              required
            />
          </Field>
          <Field>
            <Label htmlFor="user-name">Полное имя</Label>
            <Input
              id="user-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Иванов Иван Иванович"
              required
            />
          </Field>
          <Field>
            <Label htmlFor="user-dept">Департамент</Label>
            <Input
              id="user-dept"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="Юридическая служба"
            />
          </Field>
          <Field>
            <Label>Роли (можно выбрать несколько)</Label>
            <RoleChipsRow>
              {ROLES.map((r) => (
                <RoleChip
                  key={r.value}
                  type="button"
                  title={r.hint}
                  $selected={selectedRoles.includes(r.value)}
                  onClick={() => toggleRole(r.value)}
                >
                  {r.label}
                </RoleChip>
              ))}
            </RoleChipsRow>
          </Field>
          <Field>
            <Label htmlFor="user-pass">Временный пароль</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                id="user-pass"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ fontFamily: 'monospace' }}
                required
              />
              <Button
                type="button"
                $variant="secondary"
                onClick={() => setPassword(generatePassword())}
              >
                Обновить
              </Button>
            </div>
          </Field>
          <ModalActions>
            <Button type="button" $variant="ghost" onClick={close}>
              Отмена
            </Button>
            <Button type="submit" $variant="primary" disabled={isLoading}>
              {isLoading ? 'Создание…' : 'Создать'}
            </Button>
          </ModalActions>
        </form>
      )}
    </Modal>
  );
}

const CreatedBox = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.success}15;
  border: 1px solid ${({ theme }) => theme.colors.status.success}33;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const CredentialBox = styled.div`
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.8;
  word-break: break-all;
`;

function CreatedView({
  info,
  onDone,
}: {
  info: { email: string; password: string };
  onDone: () => void;
}) {
  function copy() {
    navigator.clipboard.writeText(`Email: ${info.email}\nПароль: ${info.password}`).catch(() => {});
  }
  return (
    <div>
      <CreatedBox>
        <strong>✓ Пользователь создан.</strong>
        <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
          Скопируйте логин и пароль и передайте пользователю безопасным каналом. Этот пароль больше
          нигде не показывается — после закрытия окна восстановить нельзя, можно только
          сгенерировать новый.
        </div>
        <CredentialBox>
          Email: {info.email}
          <br />
          Пароль: {info.password}
        </CredentialBox>
      </CreatedBox>
      <ModalActions>
        <Button type="button" $variant="secondary" onClick={copy}>
          Скопировать
        </Button>
        <Button type="button" $variant="primary" onClick={onDone}>
          Готово
        </Button>
      </ModalActions>
    </div>
  );
}

// ─────────── Edit modal ───────────

interface EditModalProps {
  user: AdminUser;
  onClose: () => void;
}

function EditUserModal({ user, onClose }: EditModalProps) {
  const [email, setEmail] = useState(user.email);
  const [fullName, setFullName] = useState(user.full_name);
  const [department, setDepartment] = useState(user.department ?? '');
  const [vkUserId, setVkUserId] = useState(user.vk_user_id ?? '');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    user.roles.length > 0 ? [...user.roles] : ['lawyer'],
  );
  function toggleRole(value: string) {
    setSelectedRoles((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  }
  // Пароль необязателен — оставить пустым = не менять.
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedPassword, setSavedPassword] = useState<string | null>(null);
  const [updateUser, { isLoading }] = useAdminUpdateUserMutation();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const patch: Record<string, unknown> = { id: user.id };
    if (email.trim() !== user.email) patch.email = email.trim();
    if (fullName.trim() !== user.full_name) patch.full_name = fullName.trim();
    if (department.trim() !== (user.department ?? '')) patch.department = department.trim();
    if (vkUserId.trim() !== (user.vk_user_id ?? '')) patch.vk_user_id = vkUserId.trim();
    // Сравниваем как множества (порядок не важен).
    const prevSorted = [...user.roles].sort().join(',');
    const newSorted = [...selectedRoles].sort().join(',');
    if (prevSorted !== newSorted) {
      if (selectedRoles.length === 0) {
        setError('Нельзя оставить пользователя без ролей');
        return;
      }
      patch.roles = selectedRoles;
    }
    if (password) patch.password = password;

    // Если ничего не изменилось — просто закрыть.
    if (Object.keys(patch).length === 1) {
      onClose();
      return;
    }

    try {
      await updateUser(patch as Parameters<typeof updateUser>[0]).unwrap();
      if (password) {
        // Показываем новый пароль один раз — нужно передать юзеру.
        setSavedPassword(password);
        return;
      }
      onClose();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось сохранить';
      setError(msg);
    }
  }

  function copyNewPassword() {
    if (!savedPassword) return;
    navigator.clipboard.writeText(`Email: ${email}\nПароль: ${savedPassword}`).catch(() => {});
  }

  return (
    <Modal open title={`Редактировать: ${user.full_name}`} onClose={onClose}>
      {savedPassword ? (
        <div>
          <CreatedBox>
            <strong>✓ Пароль изменён.</strong>
            <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
              Скопируйте новый пароль и передайте пользователю. После закрытия окна его нельзя будет
              посмотреть — только сгенерировать новый.
            </div>
            <CredentialBox>
              Email: {email}
              <br />
              Пароль: {savedPassword}
            </CredentialBox>
          </CreatedBox>
          <ModalActions>
            <Button type="button" $variant="secondary" onClick={copyNewPassword}>
              Скопировать
            </Button>
            <Button type="button" $variant="primary" onClick={onClose}>
              Готово
            </Button>
          </ModalActions>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Field>
            <Label htmlFor="edit-email">Email (логин)</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="edit-name">Полное имя</Label>
            <Input
              id="edit-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="edit-dept">Департамент</Label>
            <Input
              id="edit-dept"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="Юридическая служба"
            />
          </Field>
          <Field>
            <Label htmlFor="edit-vk">VK Teams userId</Label>
            <Input
              id="edit-vk"
              value={vkUserId}
              onChange={(e) => setVkUserId(e.target.value)}
              placeholder="напр. 1011816918 — для бота совещаний"
              style={{ fontFamily: 'monospace' }}
            />
          </Field>
          <Field>
            <Label>Роли (можно выбрать несколько)</Label>
            <RoleChipsRow>
              {ROLES.map((r) => (
                <RoleChip
                  key={r.value}
                  type="button"
                  title={r.hint}
                  $selected={selectedRoles.includes(r.value)}
                  onClick={() => toggleRole(r.value)}
                >
                  {r.label}
                </RoleChip>
              ))}
            </RoleChipsRow>
          </Field>
          <Field>
            <Label htmlFor="edit-pass">Новый пароль (опционально)</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                id="edit-pass"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="оставьте пустым чтобы не менять"
                style={{ fontFamily: 'monospace' }}
                minLength={8}
              />
              <Button
                type="button"
                $variant="secondary"
                onClick={() => setPassword(generatePassword())}
              >
                Сгенерировать
              </Button>
            </div>
          </Field>
          <ModalActions>
            <Button type="button" $variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" $variant="primary" disabled={isLoading}>
              {isLoading ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </ModalActions>
        </form>
      )}
    </Modal>
  );
}
